import { useRef, useState } from 'preact/hooks';
import { __ } from '../i18n.js';
import Icon from './Icon.jsx';

/**
 * HH-5: the list-item add input + history autocomplete, extracted
 * verbatim from ListDetailScreen (HH-4) now that winkelmodus needs the
 * identical behavior — same class names, so the HH-4 styling and smoke
 * expectations carry over unchanged; winkelmodus upsizes it purely via
 * its own `.etm-shop` wrapper in theme.css.
 *
 * `onAdd(label)` is synchronous by design: with the HH-5 optimistic
 * queue behind it, an add is applied locally at once, so the input
 * clears and refocuses immediately (the "two keystrokes + tap" rhythm
 * with zero waiting). Double-submit needs no busy flag anymore — the
 * first submit clears the query, so a second Enter submits emptiness
 * and the suggestion list under it is gone.
 */
export default function ItemAddInput({ history, onAdd }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  function submit(label) {
    const trimmed = label.trim();
    if (!trimmed) {
      return;
    }
    onAdd(trimmed);
    setQuery('');
    inputRef.current?.focus();
  }

  function handleSubmit(e) {
    e.preventDefault();
    submit(query);
  }

  const suggestions = query.trim()
    ? history.filter((h) => h.toLowerCase().startsWith(query.trim().toLowerCase())).slice(0, 8)
    : [];

  return (
    <form onSubmit={handleSubmit} class="etm-list-add">
      <div class="etm-list-add__input-wrap">
        <input
          ref={inputRef}
          type="text"
          class="etm-form-input"
          value={query}
          onInput={(e) => setQuery(e.currentTarget.value)}
          placeholder={__('Item toevoegen…', 'etm')}
          maxLength={140}
          autoComplete="off"
          aria-label={__('Item toevoegen', 'etm')}
        />
        {suggestions.length > 0 ? (
          <div class="etm-list-suggestions">
            {suggestions.map((label) => (
              <button
                type="button"
                key={label}
                class="etm-list-suggestion-item"
                onClick={() => submit(label)}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <button type="submit" class="etm-btn etm-btn-secondary etm-btn-sm" disabled={!query.trim()}>
        <Icon name="add" size={20} /> {__('Toevoegen', 'etm')}
      </button>
    </form>
  );
}
